// File: src/pages/TicketPurchasePage.jsx
import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import axios from 'axios';

import PaymentOptions from '../components/TicketPurchase/PayByCardSection';
import EventHeader from '../components/EventPreview/EventHeader';
import TicketGraphic from '../components/TicketPurchase/TicketGraphic';
import TicketPurchasedSheet from '../components/TicketPurchase/TicketPurchasedSheet';
import PaymentMethod from '../components/TicketPurchase/PaymentMethod';
import CheckoutForm from '../components/TicketPurchase/CheckoutForm';
import MembershipSummary from '../components/TicketPurchase/MembershipSummary';
import { useToaster } from '../context/ToasterContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

/* ------------------------------------------------------------------ */
/*                           Inner content                            */
/* ------------------------------------------------------------------ */

function TicketPurchaseContent({
  event: initialEvent,
  eventId,
  isMembershipPurchase = false,
  isMembershipUpdatePayment = false,
  updatePaymentReason = 'change',
  failedPaymentMethodId = '',
  defaultPaymentMethodId = '',
  renewalDate = '',
}) {
  const stripe = useStripe();
  const navigate = useNavigate();
  const { addToast } = useToaster();

  const toastError = (message, fallback = 'Something went wrong.') => {
    addToast(String(message || fallback), 'error', 5000);
  };

  const isMembershipFlow =
    isMembershipPurchase || isMembershipUpdatePayment;

  /* --------------------------- live logs -------------------------- */

  const [logs, setLogs] = useState([]);

  const addLog = (msg) =>
    setLogs((prev) => [
      ...prev.slice(-199),
      `[${new Date().toLocaleTimeString()}] ${msg}`,
    ]);

  /* ---------------- standard state ------------------------------- */

  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [showSheet, setShowSheet] = useState(false);
  const [sheetTicket, setSheetTicket] = useState(null);

  const wallets = [
    {
      name: 'applePay',
      label: 'Apple Pay',
    },
    {
      name: 'googlePay',
      label: 'Google Pay',
    },
  ];

  const [selectedWallet, setSelectedWallet] = useState('');
  const [savedMethods, setSavedMethods] = useState([]);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState('');
  const [addingNewCard, setAddingNewCard] = useState(false);
  const [event, setEvent] = useState(initialEvent);
  const [isRegistered, setIsRegistered] = useState(false);

  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoQuote, setPromoQuote] = useState(null);

  const cleanEventId = String(
    initialEvent?._id || eventId || ''
  ).replace(/^mixer-/, '');

  const cleanPromoCode = promoCode.trim().toUpperCase();

  const appliedPromoCode = promoQuote?.applied
    ? promoQuote.code
    : '';

  const effectivePrice = Number(
    promoQuote?.finalAmount ?? event?.price ?? 0
  );

  /* ---------------- Debugging heights ---------------- */

  useEffect(() => {
    const logHeights = () => {
      document.querySelectorAll('iframe').forEach((frame, index) => {
        const rect = frame.getBoundingClientRect();
        addLog(`iframe#${index} height=${rect.height}px`);
      });
    };

    window.addEventListener('resize', logHeights);
    logHeights();

    return () => {
      window.removeEventListener('resize', logHeights);
    };
  }, []);

  /* -------------- Check if registered -------------------- */

  useEffect(() => {
    if (isMembershipFlow || !eventId) {
      return;
    }

    const checkIfRegistered = async () => {
      const token = localStorage.getItem('accessToken');
      const userId = localStorage.getItem('userId');

      try {
        const url = eventId.startsWith('mixer-')
          ? `${process.env.REACT_APP_API_URL}/mixer/events/${eventId.replace(
              'mixer-',
              ''
            )}`
          : `${process.env.REACT_APP_API_URL}/events/${eventId}`;

        const { data } = await axios.get(url, {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        });

        const attendeeIds =
          data.attendeeIds ||
          (Array.isArray(data.attendees)
            ? data.attendees.map((attendee) => attendee._id)
            : []);

        setIsRegistered(attendeeIds.includes(userId));
      } catch (err) {
        console.error(
          'Could not determine if user is already registered',
          err
        );
      }
    };

    checkIfRegistered();
  }, [eventId, isMembershipFlow]);

  useEffect(() => {
    if (!cleanPromoCode) {
      setPromoQuote(null);
      setPromoError('');
      return;
    }

    if (promoQuote && cleanPromoCode !== promoQuote.code) {
      setPromoQuote(null);
    }
  }, [cleanPromoCode, promoQuote]);

  /* -------------- Fetch saved payment methods -------------------- */

  useEffect(() => {
    const token = localStorage.getItem('accessToken');

    axios
      .get('/api/payments/saved-methods', {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
        withCredentials: true,
      })
      .then((res) => {
        setSavedMethods(res.data);
        setSelectedSavedMethod('');
        addLog('Saved methods fetched');
      })
      .catch((error) => {
        addLog(`Error fetching saved methods: ${error.message}`);
      });
  }, []);

  /* ------------- Create Stripe PaymentRequest object ------------- */

  useEffect(() => {
    if (!stripe || !event || isMembershipUpdatePayment) {
      return;
    }

    const paymentRequestInstance = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: event.name || 'Event Payment',
        amount: Math.round(effectivePrice * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    addLog('PaymentRequest created');

    paymentRequestInstance.on('cancel', () => {
      addLog('Wallet sheet cancelled');
      setSelectedWallet('');
    });

    paymentRequestInstance.on('paymentmethod', async (paymentEvent) => {
      addLog(
        `paymentmethod event: ${paymentEvent.paymentMethod.id}`
      );

      try {
        const token = localStorage.getItem('accessToken');

        const { data } = await axios.post(
          '/api/payments/create-payment-intent',
          {
            eventId: cleanEventId,
            walletType:
              walletType === 'applePay'
                ? 'apple_pay'
                : walletType === 'googlePay'
                ? 'google_pay'
                : 'card',
            promoCode: appliedPromoCode || cleanPromoCode,
          },
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {},
            withCredentials: true,
          }
        );

        addLog(
          `PI created: ${String(
            data.clientSecret || ''
          ).slice(0, 18)}…`
        );

        const { error, paymentIntent } =
          await stripe.confirmCardPayment(
            data.clientSecret,
            {
              payment_method: paymentEvent.paymentMethod.id,
            },
            {
              handleActions: true,
            }
          );

        if (error) {
          paymentEvent.complete('fail');
          addLog(
            `confirmCardPayment error: ${error.message}`
          );
          toastError(error.message, 'Payment failed.');
          return;
        }

        paymentEvent.complete('success');
        addLog('Apple/Google Pay flow succeeded');

        await handlePaymentSuccess({
          stripePaymentIntentId: paymentIntent?.id,
          paymentMethodOverride:
            walletType === 'applePay'
              ? 'apple_pay'
              : walletType === 'googlePay'
              ? 'google_pay'
              : 'card',
        });
      } catch (error) {
        paymentEvent.complete('fail');
        addLog(
          `wallet flow exception: ${error.message}`
        );
        toastError('Payment failed. Please try again.');
      }
    });

    paymentRequestInstance.canMakePayment().then((result) => {
      addLog(`canMakePayment → ${JSON.stringify(result)}`);

      if (result?.applePay) {
        setWalletType('applePay');
        setPaymentRequest(paymentRequestInstance);
        addLog('Apple Pay available');
      } else if (result?.googlePay) {
        setWalletType('googlePay');
        setPaymentRequest(paymentRequestInstance);
        addLog('Google Pay available');
      } else {
        setWalletType(null);
        setPaymentRequest(null);
        addLog('No wallet payment available');
      }
    });
  }, [
    stripe,
    event,
    effectivePrice,
    appliedPromoCode,
    cleanPromoCode,
    isMembershipUpdatePayment,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------- Success handler -------------------- */

  const handlePaymentSuccess = async ({
    stripePaymentIntentId,
    prebuiltTicket,
    paymentMethodOverride,
  } = {}) => {
    const token = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userId');

    const isMixer =
      String(event?.mode || '').toLowerCase() === 'mixer' ||
      String(event?._id || '').startsWith('mixer-');

    const isReveal =
      String(event?.mode || '').toLowerCase() === 'reveal';

    const normalizedEventId = String(
      event?._id || ''
    ).replace(/^mixer-/, '');

    const registerUrl = `${process.env.REACT_APP_API_URL}${
      isMixer
        ? `/mixer/events/${normalizedEventId}/checkin`
        : `/events/${normalizedEventId}/register`
    }`;

    const eventUrl = `${process.env.REACT_APP_API_URL}${
      isMixer
        ? `/mixer/events/${normalizedEventId}`
        : `/events/${normalizedEventId}`
    }`;

    const ticketUrl =
      `${process.env.REACT_APP_API_URL}/tickets/${normalizedEventId}`;

    const bootToPreview = (reason = 'ended') => {
      const destination = isReveal
        ? `/events/reveal-${normalizedEventId}`
        : `/events/${normalizedEventId}`;

      navigate(destination, {
        replace: true,
        state: {
          [reason]: true,
        },
      });
    };

    try {
      if (
        selectedMethod === 'paypal' &&
        !isRegistered
      ) {
        try {
          await axios.post(
            registerUrl,
            {
              userId,
            },
            {
              headers: token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : {},
            }
          );

          setIsRegistered(true);
        } catch (error) {
          const status = error?.response?.status;
          const message = error?.response?.data?.message;

          if (
            status === 403 &&
            message === 'orientation_not_allowed'
          ) {
            bootToPreview('orientation');
            return;
          }

          if (
            status === 410 ||
            message === 'Event has ended'
          ) {
            bootToPreview('ended');
            return;
          }

          throw error;
        }
      }

      const purchaseMethod =
        selectedMethod === 'paypal'
          ? 'paypal'
          : paymentMethodOverride || 'card';

      let serverTicket = prebuiltTicket || null;

      if (!serverTicket) {
        if (effectivePrice === 0) {
          const { data } = await axios.post(
            ticketUrl,
            {
              amount: 0,
              method: 'free',
              promoCode:
                appliedPromoCode || cleanPromoCode,
            },
            {
              headers: token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : {},
              withCredentials: true,
            }
          );

          serverTicket =
            data?.userTicket || data || null;

          setIsRegistered(true);
        } else if (selectedMethod === 'paypal') {
          const { data } = await axios.post(
            ticketUrl,
            {
              amount: effectivePrice,
              method: purchaseMethod,
              promoCode:
                appliedPromoCode || cleanPromoCode,
            },
            {
              headers: token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : {},
              withCredentials: true,
            }
          );

          serverTicket =
            data?.userTicket || data || null;
        } else {
          let finalized = null;
          let lastError = null;

          for (let attempt = 0; attempt < 5; attempt += 1) {
            try {
              const { data } = await axios.post(
                '/api/payments/confirm-stripe-ticket',
                {
                  paymentIntentId:
                    stripePaymentIntentId,
                  eventId: normalizedEventId,
                  method: purchaseMethod,
                },
                {
                  headers: token
                    ? {
                        Authorization: `Bearer ${token}`,
                      }
                    : {},
                  withCredentials: true,
                }
              );

              finalized = data;
              break;
            } catch (error) {
              lastError = error;

              await new Promise((resolve) => {
                setTimeout(
                  resolve,
                  800 * (attempt + 1)
                );
              });
            }
          }

          if (!finalized) {
            throw (
              lastError ||
              new Error('Could not finalize ticket')
            );
          }

          serverTicket =
            finalized?.userTicket || null;

          setIsRegistered(true);
        }
      }

      setSheetTicket(serverTicket || null);

      try {
        const { data: updatedEvent } =
          await axios.get(eventUrl, {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {},
          });

        setEvent(updatedEvent);
      } catch (error) {
        addLog(
          `event refresh skipped: ${
            error?.response?.status ||
            error?.message
          }`
        );
      }

      addLog(
        'Registration + ticket creation succeeded.'
      );

      setShowSheet(true);
    } catch (error) {
      console.error(
        '[handlePaymentSuccess] Error full:',
        {
          message: error?.message,
          responseData:
            error?.response?.data,
          status: error?.response?.status,
          url: error?.config?.url,
          requestData:
            error?.config?.data,
        }
      );

      toastError(
        error?.response?.data?.message ||
          error?.message ||
          'There was a problem finalizing your ticket. Please contact support.'
      );
    }
  };

  /* -------- Refresh list after a new card is saved --------------- */

  const handleNewCardSuccess = async () => {
    try {
      const token =
        localStorage.getItem('accessToken');

      const { data } = await axios.get(
        '/api/payments/saved-methods',
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
          withCredentials: true,
        }
      );

      setSavedMethods(data);
      setSelectedSavedMethod('');
      setAddingNewCard(false);

      addLog(
        'Saved methods refreshed after new card'
      );
    } catch (error) {
      addLog(
        `Refresh saved methods error: ${error.message}`
      );
    }
  };

  const applyPromoCode = async () => {
    try {
      setPromoLoading(true);
      setPromoError('');

      const token =
        localStorage.getItem('accessToken') || '';

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/tickets/${cleanEventId}/discount-quote`,
        {
          promoCode: cleanPromoCode,
        },
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
          withCredentials: true,
        }
      );

      setPromoQuote(data?.quote || null);
    } catch (error) {
      setPromoQuote(null);

      setPromoError(
        error.response?.data?.message ||
          'Invalid code'
      );
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <EventHeader
        title={
          isMembershipUpdatePayment
            ? 'Update Card'
            : isMembershipPurchase
            ? 'Add Card'
            : 'Payment'
        }
      />

      {showSheet && (
        <TicketPurchasedSheet
          event={event}
          fullName={
            sheetTicket?.fullName || ''
          }
          paymentMethod={
            sheetTicket?.method ||
            (selectedWallet === 'applePay'
              ? 'apple_pay'
              : selectedWallet === 'googlePay'
              ? 'google_pay'
              : selectedMethod)
          }
          amount={
            sheetTicket?.amount ??
            effectivePrice
          }
          purchasedAt={
            sheetTicket?.purchasedAt
          }
          orderId={sheetTicket?.orderId}
          qrSrc={sheetTicket?.qrUrl}
          celebrate={true}
          onClose={() => {
            setShowSheet(false);
          }}
          onShowEvent={() => {
            const focusId = String(
              event?._id || eventId || ''
            ).replace(/^mixer-/, '');

            navigate(
              `/events?filter=registered&focusEventId=${encodeURIComponent(
                focusId
              )}`,
              {
                replace: true,
              }
            );
          }}
        />
      )}

      <div
        style={{
          opacity: addingNewCard
            ? 0.35
            : 1,
          pointerEvents: addingNewCard
            ? 'none'
            : 'auto',
          transition: 'opacity .3s',
        }}
      >
        {!isMembershipFlow && (
          <TicketGraphic event={event} />
        )}
      </div>

      {isMembershipFlow && (
        <MembershipSummary
          mode={
            isMembershipUpdatePayment
              ? 'updatePayment'
              : 'start'
          }
          reason={updatePaymentReason}
          renewalDate={renewalDate}
        />
      )}

      {!isMembershipFlow &&
        Number(event?.price || 0) > 0 && (
          <div style={styles.promoCard}>
            <div style={styles.promoTitle}>
              Promo code
            </div>

            <div style={styles.promoRow}>
              <input
                type="text"
                value={promoCode}
                onChange={(changeEvent) => {
                  setPromoCode(
                    changeEvent.target.value.toUpperCase()
                  );
                }}
                placeholder="Enter code"
                style={styles.promoInput}
              />

              <button
                type="button"
                onClick={applyPromoCode}
                disabled={
                  promoLoading ||
                  !cleanPromoCode
                }
                style={{
                  ...styles.promoApplyBtn,
                  opacity:
                    promoLoading ||
                    !cleanPromoCode
                      ? 0.55
                      : 1,
                  cursor:
                    promoLoading ||
                    !cleanPromoCode
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                {promoLoading
                  ? 'Checking…'
                  : 'Apply'}
              </button>
            </div>

            {!!promoError && (
              <div style={styles.promoError}>
                {promoError}
              </div>
            )}

            {promoQuote?.applied && (
              <div style={styles.promoSummary}>
                <div>
                  Original: $
                  {Number(
                    event?.price || 0
                  ).toFixed(2)}
                </div>

                <div>
                  Discount: -$
                  {Number(
                    promoQuote?.discountAmount ||
                      0
                  ).toFixed(2)}
                </div>

                <div
                  style={
                    styles.promoSummaryTotal
                  }
                >
                  New total: $
                  {effectivePrice.toFixed(2)}
                </div>
              </div>
            )}
          </div>
        )}

      <div
        style={{
          opacity: addingNewCard
            ? 0.35
            : 1,
          pointerEvents: addingNewCard
            ? 'none'
            : 'auto',
          transition: 'opacity .3s',
        }}
      >
        <PaymentMethod
          selectedMethod={selectedMethod}
          setSelectedMethod={
            setSelectedMethod
          }
          disablePaypal={true}
        />
      </div>

      {selectedMethod !== 'paypal' && (
        <PaymentOptions
          savedMethods={savedMethods}
          wallets={wallets}
          selectedSavedMethod={
            selectedSavedMethod
          }
          selectedWallet={selectedWallet}
          onSelectSavedMethod={
            setSelectedSavedMethod
          }
          onSelectWallet={
            setSelectedWallet
          }
          paymentRequest={paymentRequest}
          walletType={walletType}
          event={event}
          handlePaymentSuccess={
            handlePaymentSuccess
          }
          refreshSavedMethods={
            handleNewCardSuccess
          }
          onAddNewCard={setAddingNewCard}
          isFreeEvent={
            effectivePrice === 0 &&
            !isMembershipFlow
          }
          displayPrice={effectivePrice}
          promoCode={
            appliedPromoCode ||
            cleanPromoCode
          }
          isMembershipPurchase={
            isMembershipPurchase
          }
          defaultPaymentMethodId={
            defaultPaymentMethodId
          }
          isMembershipUpdatePayment={
            isMembershipUpdatePayment
          }
          updatePaymentReason={
            updatePaymentReason
          }
          failedPaymentMethodId={
            failedPaymentMethodId
          }
          handleMembershipSuccess={() => {
            navigate('/membership', {
              replace: true,
            });
          }}
        />
      )}

      {selectedMethod === 'paypal' && (
        <>
          <CheckoutForm
            event={event}
            paymentMethod="paypal"
            onSuccess={
              handlePaymentSuccess
            }
            effectivePrice={effectivePrice}
            promoCode={
              appliedPromoCode ||
              cleanPromoCode
            }
          />

          <div style={styles.paypalFooter}>
            <div style={styles.totalColumn}>
              <div
                style={styles.totalPriceRow}
              >
                <span
                  style={
                    styles.totalPriceLabel
                  }
                >
                  Total
                </span>

                <span
                  style={
                    styles.totalPriceValue
                  }
                >
                  ${effectivePrice.toFixed(2)}
                </span>
              </div>

              <div style={styles.totalSub}>
                Including all fees
              </div>
            </div>
          </div>
        </>
      )}

      {/*
      <div style={styles.logsContainer}>
        {logs.map((log, index) => (
          <div key={index} style={styles.logEntry}>
            {log}
          </div>
        ))}
      </div>
      */}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                           Page wrapper                             */
/* ------------------------------------------------------------------ */

export default function TicketPurchasePage() {
  const location = useLocation();
  const { eventId } = useParams();

  const event = location.state?.event;

  const purchaseType =
    location.state?.purchaseType ||
    'ticket';

  const isMembershipPurchase =
    purchaseType === 'membership';

  const isMembershipUpdatePayment =
    purchaseType ===
    'membershipUpdatePayment';

  const updatePaymentReason =
    location.state?.updatePaymentReason ||
    'change';

  const failedPaymentMethodId =
    location.state?.failedPaymentMethodId ||
    '';

  const defaultPaymentMethodId =
    location.state?.defaultPaymentMethodId ||
    '';

  const renewalDate =
    location.state?.renewalDate || '';

  if (!event) {
    return (
      <div>
        Loading purchase details…
      </div>
    );
  }

  return (
    <PayPalScriptProvider
      options={{
        'client-id':
          process.env
            .REACT_APP_PAYPAL_CLIENT_ID,
        currency: 'USD',
        components: 'buttons',
        intent: 'capture',
      }}
    >
      <Elements stripe={stripePromise}>
        <TicketPurchaseContent
          event={event}
          eventId={eventId}
          isMembershipPurchase={
            isMembershipPurchase
          }
          isMembershipUpdatePayment={
            isMembershipUpdatePayment
          }
          updatePaymentReason={
            updatePaymentReason
          }
          failedPaymentMethodId={
            failedPaymentMethodId
          }
          defaultPaymentMethodId={
            defaultPaymentMethodId
          }
          renewalDate={renewalDate}
        />
      </Elements>
    </PayPalScriptProvider>
  );
}

/* ------------------------------------------------------------------ */
/*                               styles                               */
/* ------------------------------------------------------------------ */

const styles = {
  container: {
    fontFamily: 'Inter, Arial, sans-serif',
    padding: 0,
    maxWidth: 480,
    margin: '0 auto',
    paddingBottom: 120,
    position: 'relative',
    minHeight: '100vh',
    boxSizing: 'border-box',
  },

  promoCard: {
    margin: '0px 8px 16px',
    padding: 14,
    borderRadius: 12,
    background: '#fff',
  },

  promoTitle: {
    fontSize: 12,
    fontWeight: 800,
    color: '#6B7280',
    textTransform: 'uppercase',
    marginBottom: 10,
  },

  promoRow: {
    display: 'flex',
    gap: 8,
  },

  promoInput: {
    flex: 1,
    height: 38,
    borderRadius: 10,
    border: '1px solid #e3e5e8ff',
    padding: '0 16px',
    fontSize: 14,
    color: '#0B0B0B',
    outline: 'none',
    boxSizing: 'border-box',
  },

  promoApplyBtn: {
    height: 36,
    borderRadius: 10,
    border: '1px solid #0B0B0B',
    background: '#0B0B0B',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    padding: '0 14px',
  },

  promoError: {
    marginTop: 8,
    marginBottom: -10,
    fontSize: 12,
    color: '#B91C1C',
    fontWeight: 700,
  },

  promoSummary: {
    marginTop: 10,
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 1.5,
  },

  promoSummaryTotal: {
    color: '#0B0B0B',
    fontWeight: 800,
    marginTop: 2,
  },

  paypalFooter: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: '#fff',
    borderTop: '1px solid #EEF0F2',
    padding:
      '16px 20px calc(16px + env(safe-area-inset-bottom, 0px))',
    zIndex: 999,
    maxWidth: 480,
    margin: '0 auto',
    boxSizing: 'border-box',
  },

  totalColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: '100%',
  },

  totalPriceRow: {
    fontFamily: "'Open Sans', sans-serif",
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 0,
  },

  totalPriceLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: '#6B7280',
    textTransform: 'uppercase',
    fontFamily: "'Open Sans', sans-serif",
  },

  totalPriceValue: {
    fontSize: 18,
    fontWeight: 900,
    color: '#0B0B0B',
    fontVariantNumeric: 'tabular-nums',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  totalSub: {
    fontSize: 12,
    fontWeight: 700,
    color: '#6B7280',
    fontFamily: "'Open Sans', sans-serif",
  },

  logsContainer: {
    position: 'fixed',
    bottom: 120,
    left: 0,
    right: 0,
    maxHeight: 300,
    minHeight: 120,
    overflowY: 'auto',
    background: 'rgba(0,0,0,0.85)',
    color: '#8fef8f',
    fontSize: 12,
    fontFamily: 'monospace',
    padding: '8px 10px',
    zIndex: 10000,
  },

  logEntry: {
    borderBottom:
      '1px solid rgba(255,255,255,0.1)',
    padding: '2px 0',
  },
};